import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { 
  ChevronDown, 
  ChevronUp, 
  TrendingUp, 
  Trophy, 
  Target,
  Clock,
  Zap,
  Shield,
  BarChart3,
  Info
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface TipDetail {
  id: string;
  sport: string;
  league: string;
  match: string;
  prediction: string;
  odds: number;
  confidence: number;
  trustScore: number;
  price: number;
  expiresAt: string;
  isAI?: boolean;
  rationale?: string;
  recentForm?: {
    homeTeam: string[];
    awayTeam: string[];
  };
  stats?: {
    label: string;
    value: string;
  }[];
}

interface InteractiveTipCarouselProps {
  tips: TipDetail[];
  onPurchase?: (tipId: string) => void;
}

export function InteractiveTipCarousel({ tips, onPurchase }: InteractiveTipCarouselProps) {
  const [expandedTips, setExpandedTips] = useState<Set<string>>(new Set());

  const toggleExpanded = (tipId: string) => {
    setExpandedTips(prev => {
      const newSet = new Set(prev);
      if (newSet.has(tipId)) {
        newSet.delete(tipId);
      } else {
        newSet.add(tipId);
      }
      return newSet;
    });
  };

  const getTrustScoreBadge = (score: number) => {
    if (score >= 85) return { label: 'Very High', color: 'bg-profit text-profit-foreground' };
    if (score >= 70) return { label: 'High', color: 'bg-premium text-premium-foreground' };
    if (score >= 55) return { label: 'Medium', color: 'bg-secondary text-secondary-foreground' };
    return { label: 'Low', color: 'bg-muted text-muted-foreground' };
  };

  const getFormIcon = (result: string) => {
    switch (result) {
      case 'W': return <span className="text-profit font-bold">W</span>;
      case 'D': return <span className="text-muted-foreground font-bold">D</span>;
      case 'L': return <span className="text-destructive font-bold">L</span>;
      default: return <span className="text-muted-foreground">-</span>;
    }
  };

  return (
    <div className="w-full px-4">
      <Carousel
        opts={{
          align: "start",
          loop: true,
        }}
        className="w-full"
      >
        <CarouselContent className="-ml-2 md:-ml-4">
          {tips.map((tip) => {
            const isExpanded = expandedTips.has(tip.id);
            const trustBadge = getTrustScoreBadge(tip.trustScore);

            return (
              <CarouselItem key={tip.id} className="pl-2 md:pl-4 md:basis-1/2 lg:basis-1/3">
                <Card className="bg-gradient-card border-border/50 hover:border-primary/30 transition-all duration-300 hover:shadow-card group">
                  <CardContent className="p-0">
                    {/* Header */}
                    <div className="p-4 pb-0">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex flex-wrap gap-2">
                          <Badge variant="outline" className="bg-secondary/50">
                            {tip.sport}
                          </Badge>
                          {tip.isAI && (
                            <Badge className="bg-gradient-premium">
                              <Zap className="h-3 w-3 mr-1" />
                              AI
                            </Badge>
                          )}
                        </div>
                        <Badge className={cn("flex items-center gap-1", trustBadge.color)}>
                          <Shield className="h-3 w-3" />
                          {trustBadge.label}
                        </Badge>
                      </div>

                      <h3 className="font-semibold text-foreground mb-1 line-clamp-2">
                        {tip.match}
                      </h3>
                      <p className="text-xs text-muted-foreground mb-3">{tip.league}</p>

                      {/* Key Metrics */}
                      <div className="grid grid-cols-3 gap-2 mb-3">
                        <div className="bg-background/50 rounded-lg p-2 border border-border/30 text-center">
                          <TrendingUp className="h-4 w-4 mx-auto mb-1 text-primary" />
                          <div className="text-lg font-bold text-foreground">{tip.odds}</div>
                          <div className="text-xs text-muted-foreground">Odds</div>
                        </div>
                        <div className="bg-background/50 rounded-lg p-2 border border-border/30 text-center">
                          <Target className="h-4 w-4 mx-auto mb-1 text-profit" />
                          <div className="text-lg font-bold text-foreground">{tip.confidence}%</div>
                          <div className="text-xs text-muted-foreground">Confidence</div>
                        </div>
                        <div className="bg-background/50 rounded-lg p-2 border border-border/30 text-center">
                          <Trophy className="h-4 w-4 mx-auto mb-1 text-premium" />
                          <div className="text-lg font-bold text-foreground">{tip.trustScore}</div>
                          <div className="text-xs text-muted-foreground">Trust</div>
                        </div>
                      </div>

                      {/* Prediction */}
                      <div className="bg-primary/10 rounded-lg p-3 mb-3 border border-primary/20">
                        <p className="font-semibold text-primary text-sm">{tip.prediction}</p>
                      </div>

                      {/* Confidence Progress */}
                      <div className="mb-3">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-muted-foreground">Model Certainty</span>
                          <span className="text-foreground font-medium">{tip.confidence}%</span>
                        </div>
                        <Progress value={tip.confidence} className="h-2" />
                      </div>

                      {/* Expand/Collapse Button */}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full mb-2"
                        onClick={() => toggleExpanded(tip.id)}
                      >
                        {isExpanded ? (
                          <>
                            <ChevronUp className="h-4 w-4 mr-2" />
                            Hide Details
                          </>
                        ) : (
                          <>
                            <Info className="h-4 w-4 mr-2" />
                            Why this prediction?
                          </>
                        )}
                      </Button>
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="p-4 pt-0 space-y-3 border-t border-border/30 mt-2 animate-in slide-in-from-top-2">
                        {/* Rationale */}
                        {tip.rationale && (
                          <div className="bg-background/50 rounded-lg p-3 border border-border/30">
                            <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                              <BarChart3 className="h-4 w-4 text-primary" />
                              Analysis
                            </h4>
                            <p className="text-xs text-muted-foreground leading-relaxed">
                              {tip.rationale}
                            </p>
                          </div>
                        )}

                        {/* Recent Form */}
                        {tip.recentForm && (
                          <div className="bg-background/50 rounded-lg p-3 border border-border/30">
                            <h4 className="text-sm font-semibold mb-2">Recent Form</h4>
                            <div className="space-y-2">
                              <div>
                                <p className="text-xs text-muted-foreground mb-1">Home</p>
                                <div className="flex gap-1">
                                  {tip.recentForm.homeTeam.map((result, idx) => (
                                    <div key={idx} className="w-6 h-6 rounded bg-background border border-border/50 flex items-center justify-center text-xs">
                                      {getFormIcon(result)}
                                    </div>
                                  ))}
                                </div>
                              </div>
                              <div>
                                <p className="text-xs text-muted-foreground mb-1">Away</p>
                                <div className="flex gap-1">
                                  {tip.recentForm.awayTeam.map((result, idx) => (
                                    <div key={idx} className="w-6 h-6 rounded bg-background border border-border/50 flex items-center justify-center text-xs">
                                      {getFormIcon(result)}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Key Stats */}
                        {tip.stats && (
                          <div className="bg-background/50 rounded-lg p-3 border border-border/30">
                            <h4 className="text-sm font-semibold mb-2">Key Statistics</h4>
                            <div className="grid grid-cols-2 gap-2">
                              {tip.stats.map((stat, idx) => (
                                <div key={idx} className="text-xs">
                                  <p className="text-muted-foreground">{stat.label}</p>
                                  <p className="font-semibold text-foreground">{stat.value}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Footer */}
                    <div className="p-4 pt-2 border-t border-border/30 space-y-2">
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          <span>Expires {new Date(tip.expiresAt).toLocaleTimeString()}</span>
                        </div>
                        <div className="font-bold text-foreground text-sm">
                          ${tip.price}
                        </div>
                      </div>
                      <Button
                        className="w-full"
                        variant={tip.price > 0 ? "premium" : "betting"}
                        onClick={() => onPurchase?.(tip.id)}
                      >
                        {tip.price > 0 ? `Buy for $${tip.price}` : 'Get Free Tip'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </CarouselItem>
            );
          })}
        </CarouselContent>
        <CarouselPrevious className="-left-4" />
        <CarouselNext className="-right-4" />
      </Carousel>
    </div>
  );
}
